import { useEffect, useRef, useState } from "react";
import "./providerfilter.css";

/* The option list is built by the caller from the loaded rows (one entry per
   provider, sorted); nothing is pinned here. The same build serves every
   practice, so a provider's name never belongs in this component. An option
   is a name, or { value, label }: `value` is what `selected` holds and the
   rows are matched on, `label` what is shown ("Dr. Malik"). */
const normalizeOptions = (options) =>
  (Array.isArray(options) ? options : [])
    .map((o) =>
      o && typeof o === "object"
        ? { value: String(o.value ?? ""), label: String(o.label ?? o.value ?? "") }
        : { value: String(o ?? ""), label: String(o ?? "") },
    )
    .filter((o) => o.value);

const CheckGlyph = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* Provider multi-select, ported from the dashboard reference. Shared by the
   dashboard and the sign-in sheet. `selected` is a
   list of option values (the dashboard's are provider-id keys, "id:20"; the
   sign-in sheet's provider names); empty means every provider, which is why
   "All Providers" simply clears it. Filtering is live - Done only dismisses.
   With ONE provider in the list, "every provider" is that provider: the
   trigger names them and looks active (owner 2026-10-07: Frisco "should
   only show 'Dr. Malik' filter active"), instead of "Provider · All". */
const ProviderFilter = ({ options, selected, setSelected }) => {
  const [open, setOpen] = useState(false);
  const opts = normalizeOptions(options);
  const labelOf = (value) => (opts.find((o) => o.value === value) || {}).label || value;
  const sole = selected.length === 0 && opts.length === 1 ? opts[0] : null;
  const isOn = (value) => selected.includes(value) || (sole && sole.value === value);
  const controlRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (controlRef.current && !controlRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const label = sole
    ? sole.label
    : selected.length === 0
      ? "All"
      : selected.length === 1
        ? labelOf(selected[0])
        : `${selected.length} providers`;

  const toggle = (name) =>
    setSelected(
      selected.includes(name)
        ? selected.filter((n) => n !== name)
        : [...selected, name],
    );

  return (
    <div className="bmpf" ref={controlRef}>
      <button
        type="button"
        className={`provider-filter-button${selected.length || sole ? " active" : ""}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        {/* Matches the reference trigger: person glyph, the PROVIDER caption,
           then the current selection. */}
        <svg
          className="provider-filter-user"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle cx="12" cy="8" r="3.2" />
          <path d="M5.5 20c.7-4 3.1-6 6.5-6s5.8 2 6.5 6" />
        </svg>
        {/* The caption only earns its place while nothing is picked. Once a
           provider is chosen the name says what this is, and it needs the
           width - it was truncating to "Dr. Mad...". */}
        {selected.length === 0 && !sole && (
          <span className="provider-filter-caption">Provider</span>
        )}
        <strong>{label}</strong>
        <svg
          className="provider-filter-chevron"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        className={`provider-filter-popover${open ? " open" : ""}`}
        aria-hidden={!open}
      >
        <div className="provider-popover-head">
          <div>
            <strong>Providers</strong>
            <span>Select one or more</span>
          </div>
          <button
            type="button"
            className="provider-popover-clear"
            onClick={() => setSelected([])}
          >
            Clear
          </button>
        </div>

        <button
          type="button"
          className={`provider-option provider-all-option${
            selected.length === 0 ? " selected" : ""
          }`}
          onClick={() => setSelected([])}
        >
          <span className="provider-option-check">
            <CheckGlyph />
          </span>
          <span className="provider-option-copy">
            <strong>All Providers</strong>
            <small>Show the full queue</small>
          </span>
        </button>

        <div className="provider-option-list">
          {opts.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`provider-option${isOn(o.value) ? " selected" : ""}`}
              onClick={() => toggle(o.value)}
            >
              <span className="provider-option-check">
                <CheckGlyph />
              </span>
              <span className="provider-option-copy">
                <strong>{o.label}</strong>
              </span>
            </button>
          ))}
        </div>

        <div className="provider-popover-foot">
          <span>
            {sole
              ? sole.label
              : selected.length === 0
                ? "All providers"
                : selected.map(labelOf).join(", ")}
          </span>
          <button type="button" onClick={() => setOpen(false)}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProviderFilter;
